import { useSignal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import QRCode from "qrcode";
import {
  apiClient,
  builderWhatsappRefLink,
  privacyPath,
  referralPayoutForTier,
  termsPath,
  upgradableTiers,
} from "../../constants.ts";
import { auth, useAuth } from "./db.ts";
import {
  AnimatedCollapsible,
  ArrowRightIcon,
  Button,
  CollapsibleCard,
  CopyButton,
  DisclosureBadge,
  Spinner,
} from "./components.tsx";
import { usePageMeta } from "./pageMeta.ts";
import {
  extractSkillNameFromSourceUrl,
  talkToSkillMetaForName,
} from "./socialMeta.ts";
import { useActiveUser } from "./loginHooks.ts";
import { toast } from "react-hot-toast";
import { builderChatPath } from "./paths.ts";
import { captureReferralFromUrl, useQueryParam } from "./utils.ts";
import {
  type AffiliateStats,
  affiliateSubtitle,
  defaultPromoLanguageId,
  getPromoLanguage,
  promoLanguages,
} from "./affiliatePromoMessages.ts";

import { DedicatedWhatsappWizard } from "./dedicatedWhatsappWizard.tsx";
import { UpgradeToEmailDialog } from "./upgradeDialog.tsx";

const talkToSkillPath = "/talk-to-skill";

type CreatedSkillBot = {
  botId?: string;
  skillName: string;
  chatLink: string;
  whatsappDemoLink: string | null;
  telegramDemoLink?: string;
  smsDemoLink?: string;
  dedicatedWaNumber?: string;
};

type SkillSuggestion = { sourceUrl: string; name: string };

const githubUrlFromPath = () => {
  const url = new URL(globalThis.location.href);
  return url.searchParams.get("url") ?? "";
};

const LinkGenerator = ({ userId }: { userId?: string }) => {
  const [url, setUrl] = useState("");
  const [generatedLink, setGeneratedLink] = useState("");

  const handleCreateAgent = (e: Event) => {
    e.preventDefault();
    if (!url) return;
    const refParam = userId ? `&ref=${userId}` : "";
    globalThis.location.href = `${talkToSkillPath}?url=${
      encodeURIComponent(url)
    }${refParam}`;
  };

  const handleGenerate = async () => {
    if (!url) return;
    const refParam = userId ? `&ref=${userId}` : "";
    const link = `${globalThis.location.origin}${talkToSkillPath}?url=${
      encodeURIComponent(url)
    }${refParam}`;
    setGeneratedLink(link);

    if (globalThis.navigator?.share) {
      try {
        await globalThis.navigator.share({
          title: "Talk to Skill | prompt2bot",
          text: `Chat with this AI agent using prompt2bot!`,
          url: link,
        });
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          await copyToClipboard(link);
        }
      }
    } else {
      await copyToClipboard(link);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await globalThis.navigator.clipboard.writeText(text);
      toast.success("Link copied to clipboard!");
    } catch {
      toast.error("Failed to copy link");
    }
  };

  return (
    <div className="flex flex-col gap-6 w-full">
      <div className="text-left space-y-4">
        <p className="text-text-secondary-light dark:text-text-secondary leading-relaxed text-center">
          Paste a skill URL (such as a GitHub repository or a Tank package like
          {" "}
          <code>tank:user/package</code>) below to instantly deploy a dedicated
          AI agent equipped with that capability.
        </p>
      </div>

      <form onSubmit={handleCreateAgent} className="flex flex-col gap-4">
        <div className="flex flex-col gap-2 text-left">
          <label className="text-sm font-medium text-text-primary-light dark:text-text-primary">
            Skill URL or Tank package
          </label>
          <input
            type="text"
            value={url}
            onInput={(e) => setUrl(e.currentTarget.value)}
            placeholder="https://github.com/... or tank:@user/package"
            className="w-full px-4 py-2 bg-surface-light dark:bg-surface border border-border-default-light dark:border-border-default rounded-lg text-text-primary-light dark:text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
            required
          />
        </div>
        <div className="flex gap-3">
          <Button
            type="submit"
            className="flex-1 justify-center py-2.5"
          >
            Create Agent
          </Button>
          <button
            type="button"
            onClick={handleGenerate}
            title="Share or copy skill link"
            className="px-4 py-2.5 flex items-center justify-center rounded-lg transition-all duration-200 bg-surface-light hover:bg-surface-overlay-light border border-border-default-light dark:bg-surface dark:hover:bg-surface-overlay dark:border-border-default text-text-primary-light dark:text-text-primary cursor-pointer shadow-sm hover:shadow-md"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-5 h-5"
            >
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
              <polyline points="16 6 12 2 8 6" />
              <line x1="12" y1="2" x2="12" y2="15" />
            </svg>
          </button>
        </div>
      </form>

      {generatedLink && (
        <div className="mt-4 p-4 bg-surface-overlay-light dark:bg-surface-overlay rounded-lg border border-border-subtle-light dark:border-border-subtle text-left space-y-3">
          <label className="text-sm font-medium text-text-primary-light dark:text-text-primary block">
            Your generated link:
          </label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              readOnly
              value={generatedLink}
              className="flex-1 bg-surface-light dark:bg-surface border border-border-default-light dark:border-border-default rounded px-3 py-1.5 text-sm text-text-primary-light dark:text-text-primary"
            />
            <CopyButton text={generatedLink} />
          </div>
        </div>
      )}
    </div>
  );
};

export const TalkToSkill = () => {
  useEffect(() => {
    captureReferralFromUrl();
  }, []);

  const [error, setError] = useState<string | null>(null);
  const status = useSignal("Preparing your skill bot...");
  const signingIn = useSignal(false);
  const creating = useSignal(false);
  const lookingUp = useSignal(false);
  const [confirmed, setConfirmed] = useState(false);
  const [createdBot, setCreatedBot] = useState<CreatedSkillBot | null>(null);
  const [existingBot, setExistingBot] = useState<CreatedSkillBot | null>(null);
  const [pendingSkillName, setPendingSkillName] = useState<string | null>(null);
  const [skillPreview, setSkillPreview] = useState<string | null>(null);
  const [suggestion, setSuggestion] = useState<SkillSuggestion | null>(null);
  const [customName, setCustomName] = useState<string>("");
  const [customPrompt, setCustomPrompt] = useState<string>("");
  const { user, isLoading: authLoading } = useAuth();
  const token = user?.refresh_token;
  const sourceUrl = useQueryParam("url");

  const activeSkillName = pendingSkillName ||
    existingBot?.skillName ||
    customName ||
    extractSkillNameFromSourceUrl(sourceUrl);

  const pageSocialMeta = talkToSkillMetaForName(activeSkillName);

  usePageMeta({
    title: pageSocialMeta.title,
    description: pageSocialMeta.description,
    image: pageSocialMeta.image,
    twitterCard: "summary_large_image",
  });

  useEffect(() => {
    if (typeof globalThis.location === "undefined") return;
    const url = new URL(globalThis.location.href);
    if (
      sourceUrl?.includes("p2b-personal-assistant") &&
      !url.searchParams.has("skip_notice") &&
      !url.searchParams.has("wizard") &&
      !url.searchParams.get("checkout")
    ) {
      globalThis.location.href = "/mentat?setup=1";
    }
  }, [sourceUrl]);

  useEffect(() => {
    if (!sourceUrl) return;
    if (authLoading) return;
    if (!user) {
      if (signingIn.value) return;
      signingIn.value = true;
      status.value = "Signing you in...";
      setError(null);
      auth.signInAsGuest().catch((err: Error) => {
        console.error(err);
        setError(`Could not sign in: ${err.message}`);
      }).finally(() => {
        signingIn.value = false;
      });
      return;
    }
    if (!token) return;
    if (existingBot || pendingSkillName || lookingUp.value) return;
    lookingUp.value = true;
    status.value = "Checking for an existing agent...";
    setError(null);
    apiClient({
      endpoint: "lookup-skill-bot",
      token,
      payload: { sourceUrl },
    }).then((result) => {
      if (!result.success) {
        if (result.suggestion) setSuggestion(result.suggestion);
        throw new Error(result.error);
      }
      if (result.skillPreview) setSkillPreview(result.skillPreview);
      if (result.existing) {
        setExistingBot({
          botId: result.botId,
          skillName: result.skillName,
          chatLink: result.chatLink,
          whatsappDemoLink: result.whatsappDemoLink,
          telegramDemoLink: result.telegramDemoLink,
          smsDemoLink: result.smsDemoLink,
        });
      } else {
        setPendingSkillName(result.skillName);
        const searchParams = new URL(globalThis.location.href).searchParams;
        const nameParam = searchParams.get("name");
        const promptParam = searchParams.get("prompt");
        setCustomName(nameParam || result.skillName);
        setCustomPrompt(
          promptParam ||
            `You are an AI agent powered by the ${result.skillName} skill. Use the installed skill to help the user with its intended task.`,
        );
      }
    }).catch((err: Error) => {
      console.error(err);
      setError(`Could not look up skill: ${err.message}`);
    }).finally(() => {
      lookingUp.value = false;
    });
  }, [authLoading, sourceUrl, user?.id, token]);

  useEffect(() => {
    if (!sourceUrl) return;
    if (!confirmed) return;
    if (!token || creating.value) return;
    creating.value = true;
    status.value = "Creating your agent...";
    setError(null);
    apiClient({
      endpoint: "create-skill-bot",
      token,
      payload: {
        sourceUrl,
        name: customName || undefined,
        prompt: customPrompt || undefined,
      },
    }).then((result) => {
      if (!result.success) throw new Error(result.error);
      status.value = `${result.skillName} is ready.`;
      setCreatedBot({
        botId: result.botId,
        skillName: result.skillName,
        chatLink: result.chatLink,
        whatsappDemoLink: result.whatsappDemoLink,
        telegramDemoLink: result.telegramDemoLink,
        smsDemoLink: result.smsDemoLink,
      });
    }).catch((err: Error) => {
      console.error(err);
      setError(`Could not create skill bot: ${err.message}`);
      creating.value = false;
    });
  }, [confirmed, sourceUrl, token]);

  const activeUser = useActiveUser();

  const heading = createdBot
    ? `${createdBot.skillName} is ready`
    : existingBot
    ? `You already have a ${existingBot.skillName} agent`
    : sourceUrl
    ? "Create your agent"
    : "Generate a Skill Link";

  const isWide = !!(createdBot || existingBot);

  return (
    <main className="flex flex-col flex-1 items-center justify-center px-4 py-16 gap-8">
      <section
        className={`${
          isWide ? "max-w-2xl xl:max-w-6xl" : "max-w-xl"
        } w-full rounded-2xl border border-border-subtle-light bg-surface-raised-light p-8 text-center shadow-xl dark:border-border-subtle dark:bg-surface-raised transition-all duration-200`}
      >
        <div className="mb-4 text-sm uppercase tracking-[0.24em] text-text-tertiary-light dark:text-text-tertiary">
          prompt2bot skill launcher
        </div>
        <h1 className="font-display text-3xl mb-6 text-text-primary-light dark:text-text-primary">
          {heading}
        </h1>
        {createdBot
          ? <ChatChoice createdBot={createdBot} token={token} />
          : existingBot
          ? <ChatChoice createdBot={existingBot} token={token} existing />
          : sourceUrl
          ? pendingSkillName && !confirmed && !error
            ? (
              <SkillLaunchConfirmation
                skillLabel={pendingSkillName}
                skillPreview={skillPreview}
                customName={customName}
                setCustomName={setCustomName}
                customPrompt={customPrompt}
                setCustomPrompt={setCustomPrompt}
                onConfirm={() => {
                  setError(null);
                  status.value = "Creating your agent...";
                  setConfirmed(true);
                }}
              />
            )
            : (
              <div className="flex flex-col items-center justify-center py-12 space-y-4 w-full text-center">
                {error
                  ? (
                    <p className="text-rose-500 font-medium text-base">
                      {error}
                    </p>
                  )
                  : (
                    <div className="flex flex-col items-center justify-center gap-3">
                      <Spinner className="h-10 w-10 text-accent" />
                      <p className="text-text-secondary-light dark:text-text-secondary font-medium text-base text-center">
                        {status.value}
                      </p>
                    </div>
                  )}
                {suggestion && (
                  <SuggestionPrompt
                    suggestion={suggestion}
                    talkToSkillPath={talkToSkillPath}
                  />
                )}
              </div>
            )
          : <LinkGenerator userId={activeUser?.id} />}

        {!sourceUrl && (
          <div className="mt-8 pt-6 border-t border-border-subtle-light dark:border-border-subtle space-y-2">
            <CreateSkillSection />
            <AffiliateSection
              activeUser={activeUser}
              token={token}
              className="w-full overflow-hidden transition-all duration-200"
              compact
            />
          </div>
        )}
      </section>

      {sourceUrl && <AffiliateSection activeUser={activeUser} token={token} />}
    </main>
  );
};

const CreateSkillSection = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="w-full overflow-hidden transition-all duration-200">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="group w-full flex items-center justify-between py-2.5 px-1 rounded-xl hover:bg-surface-overlay-light/60 dark:hover:bg-surface-overlay/60 transition-all text-left focus:outline-none cursor-pointer"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-text-primary-light dark:text-text-primary group-hover:text-accent transition-colors">
          <span>🛠️</span> Create a new skill from scratch
        </span>
        <DisclosureBadge isOpen={isOpen} />
      </button>

      <AnimatedCollapsible isOpen={isOpen}>
        <div className="py-3 px-1 space-y-3 text-left">
          <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed">
            Need a custom skill? You can build it without writing any code.
          </p>
          <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed">
            Visit the{" "}
            <a
              href={builderChatPath}
              className="text-accent underline font-semibold"
            >
              Builder AI
            </a>{" "}
            and describe the APIs, database integrations, or specific tasks you
            need. The Builder AI will design, test, package, and publish the
            skill to Tank (www.tankpkg.dev) automatically.
          </p>
        </div>
      </AnimatedCollapsible>
    </div>
  );
};

const SuggestionPrompt = (
  { suggestion, talkToSkillPath }: {
    suggestion: SkillSuggestion;
    talkToSkillPath: string;
  },
) => {
  const href = `${talkToSkillPath}?url=${
    encodeURIComponent(suggestion.sourceUrl)
  }`;
  return (
    <div className="rounded-lg border border-border-subtle-light bg-surface-overlay-light p-4 text-center dark:border-border-subtle dark:bg-surface-overlay">
      <p className="text-text-secondary-light dark:text-text-secondary">
        Did you mean{" "}
        <span className="font-semibold text-text-primary-light dark:text-text-primary break-all">
          {suggestion.name}
        </span>?
      </p>
      <a
        href={href}
        className="mt-3 inline-block rounded-md bg-accent px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
      >
        Use this skill
      </a>
    </div>
  );
};

const SkillLaunchConfirmation = (
  {
    skillLabel,
    skillPreview,
    customName,
    setCustomName,
    customPrompt,
    setCustomPrompt,
    onConfirm,
  }: {
    skillLabel: string;
    skillPreview: string | null;
    customName: string;
    setCustomName: (name: string) => void;
    customPrompt: string;
    setCustomPrompt: (prompt: string) => void;
    onConfirm: () => void;
  },
) => {
  const [showCustomization, setShowCustomization] = useState(false);

  return (
    <div className="space-y-6 text-center w-full">
      <div className="flex flex-col items-center gap-2 p-5 rounded-2xl border border-accent/20 bg-accent/5 dark:bg-accent/10 text-center">
        <span className="text-3xl mb-1">⚡</span>
        <h2 className="text-lg font-bold text-text-primary-light dark:text-text-primary">
          Deploying <span className="text-accent">{skillLabel}</span>
        </h2>
        <p className="text-sm text-text-secondary-light dark:text-text-secondary max-w-md leading-relaxed">
          This will deploy a new agent equipped with the{" "}
          <strong>{skillLabel}</strong>{" "}
          skill. You can interact on WhatsApp, Telegram, or web chat.
        </p>
      </div>

      {skillPreview && (
        <div className="relative overflow-hidden rounded-xl border border-border-subtle-light bg-surface-overlay-light p-4 text-left dark:border-border-subtle dark:bg-surface-overlay">
          <div className="max-h-40 overflow-hidden">
            <p className="text-xs font-semibold uppercase tracking-wider text-text-tertiary-light dark:text-text-tertiary mb-2">
              Skill preview
            </p>
            <pre className="whitespace-pre-wrap text-xs leading-relaxed text-text-secondary-light dark:text-text-secondary font-mono">
              {skillPreview}
            </pre>
          </div>
          <div className="absolute bottom-0 left-0 w-full h-12 bg-gradient-to-t from-surface-overlay-light to-transparent dark:from-surface-overlay pointer-events-none" />
        </div>
      )}

      <div className="w-full text-left rounded-xl border border-border-subtle-light dark:border-border-subtle bg-surface-overlay-light/60 dark:bg-surface-overlay/60 overflow-hidden transition-all">
        <button
          type="button"
          onClick={() => setShowCustomization(!showCustomization)}
          className="group w-full flex items-center justify-between p-4 text-left focus:outline-none cursor-pointer hover:bg-surface-overlay-light dark:hover:bg-surface-overlay transition-colors"
        >
          <span className="text-sm font-semibold text-text-primary-light dark:text-text-primary group-hover:text-accent transition-colors">
            Customize agent name & system prompt
            <span className="text-xs font-normal text-text-tertiary-light dark:text-text-tertiary ml-1.5">
              (optional)
            </span>
          </span>
          <DisclosureBadge isOpen={showCustomization} />
        </button>

        <AnimatedCollapsible isOpen={showCustomization}>
          <div className="p-4 pt-2 border-t border-border-subtle-light dark:border-border-subtle space-y-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-text-primary-light dark:text-text-primary">
                Agent Name
              </label>
              <input
                type="text"
                value={customName}
                onInput={(e) => setCustomName(e.currentTarget.value)}
                placeholder={skillLabel}
                className="w-full px-3 py-1.5 text-sm bg-surface-light dark:bg-surface border border-border-default-light dark:border-border-default rounded-lg text-text-primary-light dark:text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-text-primary-light dark:text-text-primary">
                System Prompt
              </label>
              <textarea
                value={customPrompt}
                onInput={(e) => setCustomPrompt(e.currentTarget.value)}
                rows={4}
                className="w-full px-3 py-1.5 text-sm bg-surface-light dark:bg-surface border border-border-default-light dark:border-border-default rounded-lg text-text-primary-light dark:text-text-primary focus:outline-none focus:ring-2 focus:ring-accent font-sans resize-y"
              />
            </div>
          </div>
        </AnimatedCollapsible>
      </div>

      <div className="flex flex-col items-center justify-center gap-3 pt-2">
        <Button
          type="button"
          className="self-center px-8 py-3.5 text-base font-semibold shadow-md hover:shadow-lg transition-all"
          onClick={onConfirm}
        >
          Create my agent
        </Button>
        <p className="text-xs text-text-tertiary-light dark:text-text-tertiary text-center">
          By creating an agent, you agree to our{" "}
          <a href={termsPath} className="underline hover:text-accent">
            Terms of Service
          </a>{" "}
          and{" "}
          <a href={privacyPath} className="underline hover:text-accent">
            Privacy Policy
          </a>
          .
        </p>
      </div>
    </div>
  );
};

export const talkToSkillRoute = talkToSkillPath;

const stableBadgeTones = {
  sky: "bg-sky-200/70 dark:bg-sky-900/70 text-sky-800 dark:text-sky-200",
  purple:
    "bg-purple-200/70 dark:bg-purple-900/70 text-purple-800 dark:text-purple-200",
  neutral: "bg-accent/10 text-accent border border-accent/20",
} as const;

const StableBadge = (
  { tone }: { tone: keyof typeof stableBadgeTones },
) => (
  <span
    className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
      stableBadgeTones[tone]
    }`}
  >
    Stable
  </span>
);

const getWhatsappWizardRedirectUrl = () => {
  if (typeof globalThis.location === "undefined") return undefined;
  const url = new URL(globalThis.location.href);
  url.searchParams.set("wizard", "whatsapp");
  return url.toString();
};

const ChatChoice = (
  { createdBot, token, existing }: {
    createdBot: CreatedSkillBot;
    token?: string;
    existing?: boolean;
  },
) => {
  const { user } = useAuth();
  const [showWizard, setShowWizard] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [dedicatedNumber, setDedicatedNumber] = useState<string | null>(
    createdBot.dedicatedWaNumber ?? null,
  );

  useEffect(() => {
    if (typeof globalThis.location === "undefined") return;
    const url = new URL(globalThis.location.href);
    if (url.searchParams.get("wizard") === "whatsapp") {
      setShowWizard(true);
      url.searchParams.delete("wizard");
      globalThis.history.replaceState({}, "", url.toString());
    }
  }, []);

  const handleMakeWhatsAppAccount = () => {
    if (!user?.email) {
      setShowUpgradeDialog(true);
      return;
    }
    setShowWizard(true);
  };
  const hasWaDemoLink = !!createdBot.whatsappDemoLink;
  const hasTgDemoLink = !!createdBot.telegramDemoLink;
  const hasSmsDemoLink = !!createdBot.smsDemoLink;
  const [waQrSvg, setWaQrSvg] = useState<string>("");
  const [tgQrSvg, setTgQrSvg] = useState<string>("");
  const [smsQrSvg, setSmsQrSvg] = useState<string>("");
  const [chatQrSvg, setChatQrSvg] = useState<string>("");

  useEffect(() => {
    if (createdBot.whatsappDemoLink) {
      QRCode.toString(createdBot.whatsappDemoLink, {
        type: "svg",
        margin: 1,
        width: 120,
        color: { dark: "#064e3b", light: "#ffffff" },
      }).then(setWaQrSvg).catch(console.error);
    }
    if (createdBot.telegramDemoLink) {
      QRCode.toString(createdBot.telegramDemoLink, {
        type: "svg",
        margin: 1,
        width: 120,
        color: { dark: "#0369a1", light: "#ffffff" },
      }).then(setTgQrSvg).catch(console.error);
    }
    if (createdBot.smsDemoLink) {
      QRCode.toString(createdBot.smsDemoLink, {
        type: "svg",
        margin: 1,
        width: 120,
        color: { dark: "#6d28d9", light: "#ffffff" },
      }).then(setSmsQrSvg).catch(console.error);
    }
    if (createdBot.chatLink) {
      QRCode.toString(createdBot.chatLink, {
        type: "svg",
        margin: 1,
        width: 120,
        color: { dark: "#111827", light: "#ffffff" },
      }).then(setChatQrSvg).catch(console.error);
    }
  }, [
    createdBot.whatsappDemoLink,
    createdBot.telegramDemoLink,
    createdBot.smsDemoLink,
    createdBot.chatLink,
  ]);

  return (
    <div className="space-y-6 text-left w-full">
      <p className="text-center text-text-secondary-light dark:text-text-secondary">
        {existing
          ? "Resume interacting with your existing agent."
          : "Select your preferred chat platform to interact with your agent."}
      </p>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {dedicatedNumber
          ? (
            <div className="group flex flex-col justify-between rounded-2xl border border-emerald-400 bg-emerald-50 p-5 text-emerald-950 shadow-sm transition hover:border-emerald-500 hover:shadow-lg dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-50 min-w-0">
              <div>
                <div className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700 dark:text-emerald-300 flex items-center justify-between">
                  <span>Dedicated WhatsApp</span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-200 dark:bg-emerald-800 text-emerald-900 dark:text-emerald-100">
                    Active
                  </span>
                </div>
                <div className="mb-2 text-xl font-semibold">
                  +{dedicatedNumber}
                </div>
                <p className="text-sm leading-relaxed text-emerald-800 dark:text-emerald-200 mb-3">
                  Your dedicated WhatsApp line is connected and ready to chat.
                </p>
              </div>
              <div className="flex flex-col items-center gap-2 pt-2">
                <a
                  href={`https://wa.me/${dedicatedNumber}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full text-center py-2.5 px-3 rounded-lg font-semibold text-sm bg-emerald-600 hover:bg-emerald-700 text-white transition-colors cursor-pointer shadow-xs inline-flex items-center justify-center gap-1.5"
                >
                  <span>Open in WhatsApp</span>
                  <ArrowRightIcon className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </a>
              </div>
            </div>
          )
          : hasWaDemoLink
          ? (
            <div className="group flex flex-col justify-between rounded-2xl border border-emerald-300/70 bg-emerald-50 p-5 text-emerald-950 shadow-sm transition hover:border-emerald-400 hover:shadow-lg dark:border-emerald-700/60 dark:bg-emerald-950/40 dark:text-emerald-50 min-w-0">
              <div>
                <div className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700 dark:text-emerald-300 flex items-center justify-between">
                  <span>WhatsApp</span>
                  <span className="text-[10px] font-normal opacity-80">
                    Demo
                  </span>
                </div>
                <div className="mb-2 text-xl font-semibold">
                  Open in WhatsApp
                </div>
                <p className="text-sm leading-relaxed text-emerald-800 dark:text-emerald-200 mb-3">
                  Uses the shared prompt2bot demo number with your agent
                  pre-loaded.
                </p>
              </div>
              <div className="flex flex-col items-center gap-2.5 pt-2">
                {waQrSvg && (
                  <div className="hidden sm:flex flex-col items-center gap-1.5 p-2 bg-white rounded-xl shadow-xs border border-emerald-200/60">
                    <div
                      className="w-[120px] h-[120px] flex items-center justify-center"
                      dangerouslySetInnerHTML={{ __html: waQrSvg }}
                    />
                    <span className="text-[11px] font-semibold text-emerald-900">
                      Scan with camera
                    </span>
                  </div>
                )}
                <a
                  href={createdBot.whatsappDemoLink!}
                  className="w-full text-center py-2.5 px-3 rounded-lg font-semibold text-sm bg-emerald-600 hover:bg-emerald-700 text-white transition-colors cursor-pointer shadow-xs inline-flex items-center justify-center gap-1.5"
                >
                  <span>Launch on WhatsApp</span>
                  <ArrowRightIcon className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </a>
                {token && createdBot.botId && (
                  <button
                    type="button"
                    onClick={handleMakeWhatsAppAccount}
                    className="w-full text-center py-2 px-3 rounded-lg font-semibold text-xs text-emerald-800 dark:text-emerald-200 hover:bg-emerald-200/50 dark:hover:bg-emerald-900/50 transition-colors cursor-pointer border border-emerald-400/50 dark:border-emerald-700/50 flex items-center justify-center gap-1.5 mt-1"
                  >
                    <span>✨ Make me a new WhatsApp account</span>
                  </button>
                )}
              </div>
            </div>
          )
          : (
            <div className="group flex flex-col justify-between rounded-2xl border border-border-subtle-light bg-surface-overlay-light p-5 text-text-secondary-light dark:border-border-subtle dark:bg-surface-overlay dark:text-text-secondary min-w-0">
              <div>
                <div className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-text-tertiary-light dark:text-text-tertiary">
                  WhatsApp
                </div>
                <div className="mb-2 text-xl font-semibold text-text-primary-light dark:text-text-primary">
                  Dedicated Line
                </div>
                <p className="text-sm leading-relaxed text-text-secondary-light dark:text-text-secondary mb-3">
                  Get a dedicated phone number for this bot with 1-click setup.
                </p>
              </div>
              <div className="pt-2">
                {token && createdBot.botId
                  ? (
                    <Button
                      onClick={handleMakeWhatsAppAccount}
                      className="w-full justify-center text-xs py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      ✨ Make me a new WhatsApp account
                    </Button>
                  )
                  : (
                    <p className="text-xs text-text-tertiary-light dark:text-text-tertiary">
                      Sign in to get a dedicated WhatsApp line.
                    </p>
                  )}
              </div>
            </div>
          )}
        {hasTgDemoLink && (
          <div className="group flex flex-col justify-between rounded-2xl border border-sky-300/70 bg-sky-50 p-5 text-sky-950 shadow-sm transition hover:border-sky-400 hover:shadow-lg dark:border-sky-700/60 dark:bg-sky-950/40 dark:text-sky-50 min-w-0">
            <div>
              <div className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-sky-700 dark:text-sky-300 flex items-center justify-between gap-2">
                <span>Telegram</span>
                <StableBadge tone="sky" />
              </div>
              <div className="mb-2 text-xl font-semibold">Open in Telegram</div>
              <p className="text-sm leading-relaxed text-sky-800 dark:text-sky-200 mb-3">
                Uses the shared Telegram demo bot to immediately chat on your
                phone or desktop.
              </p>
            </div>
            <div className="flex flex-col items-center gap-3 pt-2">
              {tgQrSvg && (
                <div className="hidden sm:flex flex-col items-center gap-1.5 p-2 bg-white rounded-xl shadow-xs border border-sky-200/60">
                  <div
                    className="w-[120px] h-[120px] flex items-center justify-center"
                    dangerouslySetInnerHTML={{ __html: tgQrSvg }}
                  />
                  <span className="text-[11px] font-semibold text-sky-900">
                    Scan with camera
                  </span>
                </div>
              )}
              <a
                href={createdBot.telegramDemoLink}
                className="w-full text-center py-2.5 px-3 rounded-lg font-semibold text-sm bg-sky-600 hover:bg-sky-700 text-white transition-colors cursor-pointer shadow-xs inline-flex items-center justify-center gap-1.5"
              >
                <span>Launch on Telegram</span>
                <ArrowRightIcon className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </a>
            </div>
          </div>
        )}
        {hasSmsDemoLink && (
          <div className="group flex flex-col justify-between rounded-2xl border border-purple-300/70 bg-purple-50 p-5 text-purple-950 shadow-sm transition hover:border-purple-400 hover:shadow-lg dark:border-purple-700/60 dark:bg-purple-950/40 dark:text-purple-50 min-w-0">
            <div>
              <div className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-purple-700 dark:text-purple-300 flex items-center justify-between gap-2">
                <span>SMS / Phone</span>
                <StableBadge tone="purple" />
              </div>
              <div className="mb-2 text-xl font-semibold">Open in SMS</div>
              <p className="text-sm leading-relaxed text-purple-800 dark:text-purple-200 mb-3">
                Uses the shared demo phone number. Opens your SMS app with the
                `/switch` command pre-filled; or dial the number to talk via
                phone.
              </p>
            </div>
            <div className="flex flex-col items-center gap-3 pt-2">
              {smsQrSvg && (
                <div className="hidden sm:flex flex-col items-center gap-1.5 p-2 bg-white rounded-xl shadow-xs border border-purple-200/60">
                  <div
                    className="w-[120px] h-[120px] flex items-center justify-center"
                    dangerouslySetInnerHTML={{ __html: smsQrSvg }}
                  />
                  <span className="text-[11px] font-semibold text-purple-900">
                    Scan with camera
                  </span>
                </div>
              )}
              <a
                href={createdBot.smsDemoLink}
                className="w-full text-center py-2.5 px-3 rounded-lg font-semibold text-sm bg-purple-600 hover:bg-purple-700 text-white transition-colors cursor-pointer shadow-xs inline-flex items-center justify-center gap-1.5"
              >
                <span>Open SMS</span>
                <ArrowRightIcon className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </a>
            </div>
          </div>
        )}
        <div className="group flex flex-col justify-between rounded-2xl border border-border-subtle-light bg-surface-overlay-light p-5 text-text-primary-light shadow-sm transition hover:border-accent hover:shadow-lg dark:border-border-subtle dark:bg-surface-overlay dark:text-text-primary min-w-0">
          <div>
            <div className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-accent flex items-center justify-between gap-2 flex-wrap">
              <span>Alice&Bot</span>
              <span className="flex items-center gap-1.5 flex-wrap justify-end">
                <StableBadge tone="neutral" />
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-accent/10 text-accent border border-accent/20 normal-case tracking-normal">
                  No account required
                </span>
              </span>
            </div>
            <div className="mb-2 text-xl font-semibold">Open web chat</div>
            <p className="text-sm leading-relaxed text-text-secondary-light dark:text-text-secondary mb-3">
              Alice&Bot is a secure, web-based chat platform featuring
              end-to-end encryption for interacting with AI agents directly from
              your browser.
            </p>
          </div>
          <div className="flex flex-col items-center gap-3 pt-2">
            {chatQrSvg && (
              <div className="hidden sm:flex flex-col items-center gap-1.5 p-2 bg-white rounded-xl shadow-xs border border-border-subtle-light">
                <div
                  className="w-[120px] h-[120px] flex items-center justify-center"
                  dangerouslySetInnerHTML={{ __html: chatQrSvg }}
                />
                <span className="text-[11px] font-semibold text-gray-900">
                  Scan with camera
                </span>
              </div>
            )}
            <a
              href={createdBot.chatLink}
              className="w-full text-center py-2.5 px-3 rounded-lg font-semibold text-sm bg-surface-raised hover:bg-surface-overlay border border-border-subtle text-text-primary transition-colors cursor-pointer shadow-xs inline-flex items-center justify-center gap-1.5"
            >
              <span>Launch Web Chat</span>
              <ArrowRightIcon className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </a>
          </div>
        </div>
      </div>
      <div className="mt-6 border-t border-border-subtle-light dark:border-border-subtle pt-4 text-center">
        <p className="text-xs text-text-tertiary-light dark:text-text-tertiary">
          WhatsApp demo number not working? Check the status and availability of
          our shared numbers on the{" "}
          <a
            href="/demo-numbers"
            className="underline hover:text-accent font-medium"
          >
            WhatsApp Demo Status page
          </a>.
        </p>
      </div>
      {showUpgradeDialog && (
        <UpgradeToEmailDialog
          isOpen={showUpgradeDialog}
          onClose={() => setShowUpgradeDialog(false)}
          onSuccess={() => setShowWizard(true)}
          redirectUrl={getWhatsappWizardRedirectUrl()}
          title="Add Email to Get a Dedicated Number"
          description="To set up a dedicated WhatsApp number, add an email to your account so you can manage your numbers and billing."
        />
      )}
      {showWizard && createdBot.botId && token && (
        <DedicatedWhatsappWizard
          botId={createdBot.botId}
          token={token}
          isOpen={showWizard}
          onClose={() => setShowWizard(false)}
          onSuccess={(phone) => {
            setDedicatedNumber(phone);
          }}
        />
      )}
    </div>
  );
};

export const AffiliateSection = (
  { activeUser, token, className, compact }: {
    activeUser: { id: string } | null | undefined;
    token?: string;
    className?: string;
    compact?: boolean;
  },
) => {
  const [isOpen, setIsOpen] = useState(false);
  const [stats, setStats] = useState<AffiliateStats | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!activeUser?.id || !token) return;
    setLoading(true);
    apiClient({
      endpoint: "get-affiliate-stats",
      token,
      payload: {},
    })
      .then((res) => {
        if (res.success) setStats(res);
      })
      .catch((err) => console.error("Failed to load affiliate stats:", err))
      .finally(() => setLoading(false));
  }, [activeUser?.id, token]);

  const subtitle = affiliateSubtitle(loading, stats);

  if (!compact) {
    return (
      <CollapsibleCard
        title="🎁 Refer & Earn 20% Recurring"
        subtitle={subtitle}
        className={className}
      >
        <div className="space-y-5 text-left">
          {activeUser?.id && token
            ? (
              <AffiliateDashboard
                userId={activeUser.id}
                token={token}
                stats={stats}
                loading={loading}
              />
            )
            : <AffiliatePromo />}
        </div>
      </CollapsibleCard>
    );
  }

  return (
    <div
      className={className ??
        "max-w-xl w-full border border-border-subtle-light dark:border-border-subtle rounded-2xl bg-surface-raised-light dark:bg-surface-raised shadow-md overflow-hidden transition-all duration-200"}
    >
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="group w-full flex items-center justify-between text-left font-medium text-text-primary-light dark:text-text-primary focus:outline-none transition-all cursor-pointer py-2.5 px-1 rounded-xl hover:bg-surface-overlay-light/60 dark:hover:bg-surface-overlay/60"
      >
        <span className="flex items-center gap-2 font-semibold text-text-primary-light dark:text-text-primary group-hover:text-accent transition-colors text-sm">
          <span>🎁</span> Refer & Earn 20% Recurring
        </span>
        <div className="flex items-center gap-2 shrink-0">
          {stats && (
            <span className="text-xs font-semibold text-text-secondary-light dark:text-text-secondary">
              ${stats.earnings}/mo
            </span>
          )}
          <DisclosureBadge isOpen={isOpen} />
        </div>
      </button>

      <AnimatedCollapsible isOpen={isOpen}>
        <div className="space-y-5 text-left py-3 px-1">
          {activeUser?.id && token
            ? (
              <AffiliateDashboard
                userId={activeUser.id}
                token={token}
                stats={stats}
                loading={loading}
              />
            )
            : <AffiliatePromo />}
        </div>
      </AnimatedCollapsible>
    </div>
  );
};

const PayoutBreakdown = () => (
  <div className="text-xs text-text-tertiary-light dark:text-text-tertiary space-y-1">
    <p className="font-semibold uppercase tracking-wider">Per-tier payout</p>
    <ul className="space-y-0.5">
      {upgradableTiers.map((t) => (
        <li key={t.name}>
          {t.name} (${t.price}/mo) →{" "}
          <strong>
            ${referralPayoutForTier(t.name)}/mo
          </strong>
        </li>
      ))}
    </ul>
  </div>
);

const AffiliatePromo = () => (
  <div className="text-left space-y-3">
    <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed">
      Join our affiliate program. Share custom skill launcher links to earn a
      {" "}
      <strong>20% recurring monthly commission</strong>{" "}
      on any referred users who upgrade to a paid plan.
    </p>
    <PayoutBreakdown />
    <p className="text-xs text-text-tertiary-light dark:text-text-tertiary leading-relaxed">
      Log in or launch a skill agent to retrieve your personal referral link and
      track your earnings in real time.
    </p>
  </div>
);

const StatBox = (
  { value, label, colorClass }: {
    value: string | number;
    label: string;
    colorClass: string;
  },
) => (
  <div className="text-center p-3 bg-surface-light dark:bg-surface rounded-xl">
    <div className={`text-2xl font-bold ${colorClass}`}>
      {value}
    </div>
    <div className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary-light dark:text-text-tertiary mt-1">
      {label}
    </div>
  </div>
);

const ReferralLinkField = (
  { label, value }: { label: string; value: string },
) => (
  <div className="space-y-2">
    <label className="text-xs font-semibold text-text-tertiary-light dark:text-text-tertiary uppercase tracking-wider block">
      {label}
    </label>
    <div className="flex items-center gap-2">
      <input
        type="text"
        readOnly
        value={value}
        className="flex-1 bg-surface-light dark:bg-surface border border-border-default-light dark:border-border-default rounded-lg px-3 py-2 text-sm text-text-primary-light dark:text-text-primary focus:outline-none"
      />
      <CopyButton text={value} />
    </div>
  </div>
);

const ShareablePromoMessage = (
  { websiteLink, whatsappLink }: {
    websiteLink: string;
    whatsappLink: string;
  },
) => {
  const [selectedLangId, setSelectedLangId] = useState(defaultPromoLanguageId);
  const lang = getPromoLanguage(selectedLangId);
  const message = lang.format({ websiteLink, whatsappLink });

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <label className="text-xs font-semibold text-text-tertiary-light dark:text-text-tertiary uppercase tracking-wider block">
          Shareable promotional message:
        </label>
        <div className="flex items-center gap-2">
          <select
            value={selectedLangId}
            onChange={(e) =>
              setSelectedLangId((e.target as HTMLSelectElement).value)}
            className="bg-surface-light dark:bg-surface border border-border-default-light dark:border-border-default rounded-lg px-2.5 py-1 text-xs text-text-primary-light dark:text-text-primary focus:outline-none cursor-pointer"
          >
            {promoLanguages.map((l) => (
              <option key={l.id} value={l.id}>
                {l.label}
              </option>
            ))}
          </select>
          <CopyButton text={message} />
        </div>
      </div>
      <textarea
        readOnly
        dir={lang.isRtl ? "rtl" : "ltr"}
        value={message}
        rows={9}
        className="w-full bg-surface-light dark:bg-surface border border-border-default-light dark:border-border-default rounded-lg p-3 text-xs leading-relaxed text-text-primary-light dark:text-text-primary focus:outline-none resize-none font-sans"
      />
      <p className="text-xs text-text-tertiary-light dark:text-text-tertiary">
        Pre-written message with your referral links embedded, ready to copy and
        share.
      </p>
    </div>
  );
};

const AffiliateDashboard = (
  { userId, token, stats, loading }: {
    userId: string;
    token: string;
    stats: AffiliateStats | null;
    loading: boolean;
  },
) => {
  const [submittingPayout, setSubmittingPayout] = useState(false);
  const [payoutSuccess, setPayoutSuccess] = useState(false);
  const [payoutError, setPayoutError] = useState<string | null>(null);

  const handleRequestPayout = () => {
    if (!stats || stats.earnings < 50 || submittingPayout) return;
    setSubmittingPayout(true);
    setPayoutError(null);
    apiClient({
      endpoint: "request-affiliate-payout",
      token,
      payload: {},
    })
      .then((res) => {
        if (res.success) {
          setPayoutSuccess(true);
          toast.success("Payout request sent successfully!");
        } else {
          setPayoutError(res.error || "Failed to submit request.");
        }
      })
      .catch((err) => {
        console.error("Payout request failed:", err);
        setPayoutError("An error occurred. Please try again.");
      })
      .finally(() => {
        setSubmittingPayout(false);
      });
  };

  const currentSkillUrl =
    typeof globalThis !== "undefined" && globalThis.location
      ? githubUrlFromPath()
      : "";
  const origin = typeof globalThis !== "undefined" && globalThis.location
    ? globalThis.location.origin
    : "";
  const referralLink = currentSkillUrl
    ? `${origin}${talkToSkillPath}?url=${
      encodeURIComponent(currentSkillUrl)
    }&ref=${userId}`
    : `${origin}/?ref=${userId}`;

  return (
    <div className="text-left space-y-5">
      <div className="space-y-3">
        <p className="text-sm text-text-secondary-light dark:text-text-secondary">
          Share your personal referral link or any custom skill launcher link.
          When referred users upgrade to a paid plan, you will earn a{" "}
          <strong>20% recurring monthly commission</strong>.
        </p>
        <PayoutBreakdown />
      </div>

      <ReferralLinkField
        label={currentSkillUrl
          ? "Your skill referral link:"
          : "Your personal referral link:"}
        value={referralLink}
      />

      <div className="space-y-2">
        <ReferralLinkField
          label="Your WhatsApp referral link:"
          value={builderWhatsappRefLink(userId)}
        />
        <p className="text-xs text-text-tertiary-light dark:text-text-tertiary">
          Opens a WhatsApp chat with the prompt2bot builder bot — anyone new who
          starts chatting through it is credited to you.
        </p>
      </div>

      <ShareablePromoMessage
        websiteLink={referralLink}
        whatsappLink={builderWhatsappRefLink(userId)}
      />

      {loading
        ? (
          <div className="text-sm text-text-secondary-light dark:text-text-secondary animate-pulse py-2">
            Loading your referral stats...
          </div>
        )
        : stats
        ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-2 border-t border-border-subtle-light dark:border-border-subtle">
            <StatBox
              value={stats.visitsCount}
              label="Visits"
              colorClass="text-sky-500"
            />
            <StatBox
              value={stats.signupsCount}
              label="Signups"
              colorClass="text-accent"
            />
            <StatBox
              value={stats.conversionsCount}
              label="Paying"
              colorClass="text-emerald-500"
            />
            <StatBox
              value={`$${stats.earnings}/mo`}
              label="Earnings"
              colorClass="text-indigo-500"
            />
          </div>
        )
        : null}

      {stats && !loading && (
        <div className="space-y-4 pt-2 border-t border-border-subtle-light dark:border-border-subtle">
          {payoutSuccess
            ? (
              <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-sm text-center">
                🎉 <strong>Payout Request Sent</strong>
                <p className="mt-1 text-xs text-emerald-600/80 dark:text-emerald-400/80">
                  Our team has been notified. We will review and process your
                  payout shortly.
                </p>
              </div>
            )
            : (
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-text-secondary-light dark:text-text-secondary">
                    Payout Status:
                  </span>
                  <span className="text-sm font-semibold text-text-primary-light dark:text-text-primary">
                    {stats.earnings >= 50
                      ? (
                        <span className="text-emerald-500 font-bold">
                          Eligible (&gt;= $50)
                        </span>
                      )
                      : (
                        <span className="text-text-tertiary-light dark:text-text-tertiary">
                          Minimum threshold: $50.00
                        </span>
                      )}
                  </span>
                </div>

                {payoutError && (
                  <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/20 text-rose-500 text-xs text-center font-semibold">
                    {payoutError}
                  </div>
                )}

                <Button
                  type="button"
                  onClick={handleRequestPayout}
                  disabled={stats.earnings < 50 || submittingPayout}
                  className={`w-full justify-center py-2 text-sm font-semibold transition rounded-lg ${
                    stats.earnings >= 50
                      ? "bg-accent hover:opacity-90 text-white cursor-pointer"
                      : "bg-surface-light dark:bg-surface border border-border-default-light dark:border-border-default text-text-tertiary-light dark:text-text-tertiary cursor-not-allowed"
                  }`}
                >
                  {submittingPayout ? "Sending request..." : "Request Payout"}
                </Button>
              </div>
            )}
        </div>
      )}

      <div className="text-center pt-2 border-t border-border-subtle-light dark:border-border-subtle">
        <p className="text-[10px] text-text-tertiary-light dark:text-text-tertiary leading-relaxed">
          Payouts are processed manually. To claim your accrued earnings, please
          email us at{" "}
          <a
            href="mailto:billing@prompt2bot.com"
            className="text-accent underline font-semibold"
          >
            billing@prompt2bot.com
          </a>
          .
        </p>
      </div>
    </div>
  );
};
